import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';
import environment from '../config/environment';

const REGISTROS_DOBLADA = ['OTRO', 'OTRO-V'];

const esRegistroDoblada = (registro) =>
  REGISTROS_DOBLADA.includes(String(registro || '').trim().toUpperCase());

const esOtroVacante = (registro) =>
  String(registro || '').trim().toUpperCase() === 'OTRO-V';

const nombrePlanificadoFila = (row) => {
  if (!row) return null;
  if (esOtroVacante(row.registro_manual)) return 'VACANTE';
  return row.nombre_planificado || null;
};

const ControlRelevos = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [turno, setTurno] = useState(null);
  const [fechaServidor, setFechaServidor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [ciudadFiltro, setCiudadFiltro] = useState('TODAS');
  const [countdown, setCountdown] = useState(5);
  const [guardiasAsignados, setGuardiasAsignados] = useState([]);
  const [loadingGuardias, setLoadingGuardias] = useState(false);

  // ===== Estado nuevo: Asignar Doblada (solo SOC + OTRO/OTRO-V sin cubridor) =====
  const [dobladaOpen, setDobladaOpen] = useState(false);
  const [dobladaRow, setDobladaRow] = useState(null);
  const [dobladaSearch, setDobladaSearch] = useState('');
  const [dobladaResults, setDobladaResults] = useState([]);
  const [dobladaSelected, setDobladaSelected] = useState(null);
  const [dobladaLoadingResults, setDobladaLoadingResults] = useState(false);
  const [dobladaError, setDobladaError] = useState('');
  const [dobladaConfirmOpen, setDobladaConfirmOpen] = useState(false);
  const [dobladaSubmitting, setDobladaSubmitting] = useState(false);
  const [dobladaSuccessOpen, setDobladaSuccessOpen] = useState(false);
  const [dobladaShowResults, setDobladaShowResults] = useState(false);
  const [controlAutomatico, setControlAutomatico] = useState(false);

  // Refs estables que persisten entre renders (CORRECTO: fuera del useEffect)
  const dataHashRef = useRef(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const isRunningRef = useRef(false);
  const isVisibleRef = useRef(!document.hidden);
  const isInitialLoadRef = useRef(true);

  // Debug: Verificar que la variable de entorno se esté leyendo
  useEffect(() => {
    console.log('🔧 REACT_APP_CHAR desde process.env:', process.env.REACT_APP_CHAR);
    console.log('🔧 puntoChar desde environment:', environment.puntoChar);
  }, []);

  useEffect(() => {
    // Solo hacer fetch si el usuario es supervisor
    if (!user || user?.rol !== 'S') {
      return;
    }

    const fetchData = async (showLoader = false) => {
      // Verificar flags antes de hacer fetch
      if (!isVisibleRef.current || !isRunningRef.current) {
        return;
      }

      if (showLoader) {
      setError(null);
      }

      try {
        // Una sola llamada: el backend calcula turno, fecha y devuelve datos
        // El backend es la fuente de verdad y solo consulta cuando se le solicita
        const response = await fetch(
          `${buildApiUrl('/relevos/control')}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Error al obtener los datos');
        }

        if (result.success) {
          // El backend siempre devuelve turno y fecha calculados
          const turnoCalculado = result.turno;
          const fechaDelServidor = result.fecha;
          
          // Actualizar turno y fecha si cambiaron
          setTurno(prevTurno => {
            if (prevTurno !== turnoCalculado) {
              return turnoCalculado;
            }
            return prevTurno;
          });
          setFechaServidor(prevFecha => {
            if (prevFecha !== fechaDelServidor) {
              return fechaDelServidor;
            }
            return prevFecha;
          });

          setControlAutomatico(result.control_automatico === true);

          // Solo actualizar datos si el hash cambió (optimización de datos móviles)
          // El hash incluye datos + turno + fecha, así detectamos cualquier cambio
          if (result.hash && result.hash !== dataHashRef.current) {
          setData(result.data || []);
            dataHashRef.current = result.hash;
          } else if (!result.hash) {
            // Si el backend no devuelve hash, comparar datos directamente
            setData(currentData => {
              const newDataString = JSON.stringify(result.data || []);
              const currentDataString = JSON.stringify(currentData);
              if (newDataString !== currentDataString) {
                return result.data || [];
              }
              return currentData;
            });
          }
          // Si el hash es igual, no actualizamos (ahorra re-renders y consumo de datos)
        } else {
          throw new Error(result.error || 'Error al obtener los datos');
        }
      } catch (err) {
        console.error('Error fetching control de relevos:', err);
        if (showLoader) {
        setError(err.message || 'Error al cargar los datos');
        }
      } finally {
        if (showLoader) {
        setLoading(false);
      }
      }
    };

    // Contador regresivo para mostrar tiempo hasta próxima actualización
    const startCountdown = () => {
      // Limpiar intervalo anterior si existe
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      
      setCountdown(5);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
            return 5; // Reiniciar a 5
          }
          return prev - 1;
        });
      }, 1000);
    };

    const stopCountdown = () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };

    // Funciones de control del polling con doble control
    const startPolling = () => {
      // Doble verificación: evita duplicados
      if (isRunningRef.current || !isVisibleRef.current) {
        return;
      }
      
      isRunningRef.current = true;
      
      // Primera llamada inmediata
      if (isInitialLoadRef.current) {
        setLoading(true);
        fetchData(true).then(() => {
          isInitialLoadRef.current = false;
          startCountdown();
        });
      } else {
        fetchData(false);
        startCountdown();
      }
      
      // Configurar polling cada 5 segundos
      intervalRef.current = setInterval(() => {
        // Verificar flags antes de ejecutar
        if (isRunningRef.current && isVisibleRef.current) {
          fetchData(false);
          startCountdown();
        }
      }, 5000);
    };

    const stopPolling = () => {
      isRunningRef.current = false;
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      stopCountdown();
    };

    // Manejar visibilidad de la página (Page Visibility API)
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      
      if (isVisibleRef.current) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Registrar listener de visibilidad
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Inicializar visibilidad actual
    isVisibleRef.current = !document.hidden;

    // Inicio seguro
    if (isVisibleRef.current) {
      startPolling();
    }

    // Cleanup
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // ===== Efecto: cargar lista de cubridores cuando el modal está abierto y hay texto (con debounce) =====
  useEffect(() => {
    if (!controlAutomatico || !dobladaOpen || !dobladaRow) return undefined;
    // Solo consultar cuando el usuario haya escrito algo, para no consumir datos al solo abrir el modal
    if (!dobladaSearch || !dobladaSearch.trim()) {
      setDobladaResults([]);
      setDobladaLoadingResults(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setDobladaLoadingResults(true);
      try {
        const params = new URLSearchParams();
        if (fechaServidor) params.append('fecha', fechaServidor);
        if (turno) params.append('turno', turno);
        if (dobladaRow.id_generado) {
          params.append('destino', String(dobladaRow.id_generado));
        }
        if (dobladaRow.cedula_planificada) {
          params.append('excluirCedula', String(dobladaRow.cedula_planificada));
        }
        params.append('q', dobladaSearch.trim());
        const response = await fetch(
          `${buildApiUrl('/relevos/cubridores')}?${params.toString()}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          }
        );
        const result = await response.json();
        if (result.success) {
          setDobladaResults(Array.isArray(result.data) ? result.data : []);
        } else {
          setDobladaResults([]);
        }
      } catch (err) {
        console.error('Error cargando cubridores:', err);
        setDobladaResults([]);
      } finally {
        setDobladaLoadingResults(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [controlAutomatico, dobladaOpen, dobladaRow, dobladaSearch, fechaServidor, turno]);

  const abrirModalDoblada = (row) => {
    setDobladaRow(row);
    setDobladaSearch('');
    setDobladaResults([]);
    setDobladaSelected(null);
    setDobladaError('');
    setDobladaShowResults(false);
    setDobladaConfirmOpen(false);
    setDobladaSuccessOpen(false);
    setDobladaOpen(true);
  };

  const cerrarModalDoblada = () => {
    setDobladaOpen(false);
    setDobladaConfirmOpen(false);
    setDobladaRow(null);
    setDobladaSearch('');
    setDobladaResults([]);
    setDobladaSelected(null);
    setDobladaError('');
    setDobladaShowResults(false);
    setDobladaSubmitting(false);
  };

  const submitDoblada = async () => {
    if (!dobladaRow || !dobladaSelected) return;
    setDobladaSubmitting(true);
    setDobladaError('');
    try {
      const response = await fetch(buildApiUrl('/relevos/asignar-cubridor'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clave: dobladaRow.clave,
          cedulaCubridor: dobladaSelected.cedula,
          usuario: (user?.login_nombre || user?.nick || user?.firstName || user?.username || '').trim(),
          motivo_app: 'DOBLA'
        })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'No se pudo registrar la doblada');
      }
      // Forzar que el próximo polling sí actualice la UI
      dataHashRef.current = null;
      setDobladaConfirmOpen(false);
      setDobladaOpen(false);
      setDobladaSuccessOpen(true);
    } catch (err) {
      setDobladaError(err.message || 'Error al registrar la doblada');
    } finally {
      setDobladaSubmitting(false);
    }
  };

  // Validar que solo los supervisores puedan acceder
  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No hay usuario autenticado.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (user?.rol !== 'S') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No tienes permisos para acceder a esta sección. Solo los supervisores pueden acceder.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header fijo al hacer scroll */}
        <div className="sticky top-0 z-40 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 mb-6 border-b border-gray-200 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Control de Relevos</h1>
              <p className="mt-2 text-sm text-gray-600">Gestiona y supervisa los relevos realizados</p>
            </div>
            {/* Contador de puntos pendientes */}
            {data.length > 0 && (
              <div className="flex-shrink-0">
                <div className="flex flex-col items-center gap-2">
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl shadow-lg px-4 py-3 border-2 border-orange-600 animate-pulse" style={{
                  animation: 'pulse-subtle 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}>
                  <style>{`
                    @keyframes pulse-subtle {
                      0%, 100% {
                        opacity: 1;
                      }
                      50% {
                        opacity: 0.7;
                      }
                    }
                  `}</style>
                  <div className="flex items-center justify-center gap-3">
                    <svg className="w-6 h-6 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-xs font-semibold text-white uppercase tracking-wide leading-tight">Pendientes</span>
                      <span className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                        {data.filter(row => row.HORA_RELEVO === 'PENDIENTE').length}
                      </span>
                    </div>
                  </div>
                  </div>
                  {!loading && (
                    <div className="flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                        Actualizando en: <span className="font-semibold text-indigo-600">{countdown}s</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Información del turno y fecha */}
        {turno && fechaServidor && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center flex-shrink-0">
                  <span className="text-xs font-medium text-gray-700 mr-2">Turno actual:</span>
                  <span className="text-xs font-semibold text-indigo-600">{turno}</span>
                </div>
                  <div className="flex items-center flex-shrink-0">
                  <span className="text-xs font-medium text-gray-700 mr-2">Fecha:</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {new Date(fechaServidor + 'T00:00:00').toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
                {/* Último relevo */}
                {(() => {
                  // Filtrar relevos que no sean PENDIENTE y extraer hora
                  const relevosConHora = data
                    .filter(row => row.HORA_RELEVO && row.HORA_RELEVO !== 'PENDIENTE')
                    .map(row => {
                      // HORA_RELEVO viene como "YYYY-MM-DD HH:MM:SS" desde el backend
                      let horaStr = '';
                      let horaDate = null;
                      
                      if (row.HORA_RELEVO.includes(' ')) {
                        // Separar fecha y hora
                        const partes = row.HORA_RELEVO.split(' ');
                        horaStr = partes[1] || ''; // Parte de hora (HH:MM:SS)
                        
                        // Parsear como fecha completa para comparar correctamente
                        try {
                          horaDate = new Date(row.HORA_RELEVO);
                          if (isNaN(horaDate.getTime())) {
                            // Si falla, intentar parsear solo la hora
                            if (horaStr && horaStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
                              const [hours, minutes, seconds] = horaStr.split(':').map(Number);
                              horaDate = new Date();
                              horaDate.setHours(hours, minutes, seconds || 0);
                            }
                          }
                        } catch (e) {
                          // Si falla, intentar parsear solo la hora
                          if (horaStr && horaStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
                            const [hours, minutes, seconds] = horaStr.split(':').map(Number);
                            horaDate = new Date();
                            horaDate.setHours(hours, minutes, seconds || 0);
                          }
                        }
                      } else {
                        // Si solo viene la hora (caso raro)
                        horaStr = row.HORA_RELEVO;
                        if (horaStr && horaStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
                          const [hours, minutes, seconds] = horaStr.split(':').map(Number);
                          horaDate = new Date();
                          horaDate.setHours(hours, minutes, seconds || 0);
                        }
                      }
                      
                      return {
                        hora: horaDate,
                        horaStr: horaStr,
                        punto: row.punto,
                        row: row
                      };
                    })
                    .filter(item => item.hora !== null && !isNaN(item.hora.getTime()) && item.horaStr);
                  
                  if (relevosConHora.length > 0) {
                    // Encontrar el relevo más tardío (mayor hora)
                    const ultimoRelevo = relevosConHora.reduce((max, current) => {
                      return current.hora > max.hora ? current : max;
                    });
                    
                    // Formatear hora como HH:MM:SS
                    const horaFormateada = ultimoRelevo.horaStr;
                    const puntoFormateado = ultimoRelevo.punto 
                      ? `${environment.puntoChar || ''}${ultimoRelevo.punto}`
                      : '';
                    
                    return (
                      <div className="flex items-center flex-shrink-0">
                        <span className="text-xs font-medium text-gray-700 mr-2">Último relevo:</span>
                        <span className="text-xs font-semibold text-gray-900">
                          {horaFormateada} {puntoFormateado && `(${puntoFormateado})`}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="flex items-center flex-shrink-0 sm:ml-auto">
                <label htmlFor="ciudad-filtro" className="text-xs font-medium text-gray-700 mr-2 whitespace-nowrap">
                  Ciudad:
                </label>
                <select
                  id="ciudad-filtro"
                  value={ciudadFiltro}
                  onChange={(e) => setCiudadFiltro(e.target.value)}
                  className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-w-[120px]"
                >
                  <option value="TODAS">Todas</option>
                  {Array.from(new Set(data.map(row => row.ciudad?.toUpperCase()).filter(Boolean))).sort().map(ciudad => (
                    <option key={ciudad} value={ciudad}>{ciudad}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de datos */}
        {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando datos...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punto
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hora Relevo / Registro
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-2 py-3 text-center text-xs text-gray-500">
                        No se encontraron registros para los filtros seleccionados
                      </td>
                    </tr>
                  ) : (
                    data
                      .filter(row => {
                        if (ciudadFiltro === 'TODAS') return true;
                        return row.ciudad?.toUpperCase() === ciudadFiltro;
                      })
                      .map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-2 py-2 text-xs font-medium text-gray-900 align-top">
                          <div className="flex flex-col items-start gap-1">
                            <span>{row.punto ? `${environment.puntoChar || ''}${row.punto}` : '-'}</span>
                            {controlAutomatico && row.registro_relevo === 'PLATAFORMA' && esRegistroDoblada(row.registro_manual) && !row.cedula_registrada && nombrePlanificadoFila(row) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrirModalDoblada(row);
                                }}
                                className="p-1 rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                title="Asignar doblada (quién cubrió este puesto)"
                                aria-label="Asignar doblada"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-900 align-top break-words">
                          <div>{row.nombre || '-'}</div>
                          {controlAutomatico && row.registro_relevo === 'PLATAFORMA' && esRegistroDoblada(row.registro_manual) && row.cedula_registrada && nombrePlanificadoFila(row) && (
                            <p className="mt-1 text-[10px] leading-tight text-gray-600 break-words">
                              <span className="font-semibold text-gray-800">
                                {row.nombre_registrado || '—'}
                              </span>
                              {' dobla por '}
                              <span className="font-semibold text-gray-800">
                                {nombrePlanificadoFila(row)}
                              </span>
                            </p>
                          )}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <button
                            onClick={async () => {
                              setSelectedRow(row);
                              setShowModal(true);
                              
                              // Si el relevo es PENDIENTE, cargar los guardias asignados
                              if (row.HORA_RELEVO === 'PENDIENTE' && row.id_generado) {
                                setLoadingGuardias(true);
                                setGuardiasAsignados([]);
                                try {
                                  const response = await fetch(
                                    `${buildApiUrl(`/relevos/guardias-asignados/${row.id_generado}`)}`,
                                    {
                                      method: 'GET',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      credentials: 'include',
                                    }
                                  );
                                  const result = await response.json();
                                  if (result.success) {
                                    setGuardiasAsignados(result.data || []);
                                  }
                                } catch (err) {
                                  console.error('Error cargando guardias asignados:', err);
                                } finally {
                                  setLoadingGuardias(false);
                                }
                              } else {
                                setGuardiasAsignados([]);
                              }
                            }}
                            className={`w-full px-2 py-2 rounded-md text-left transition-all duration-200 shadow-sm hover:shadow-md group ${
                              row.HORA_RELEVO === 'PENDIENTE'
                                ? 'bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 border border-orange-200 hover:border-orange-300'
                                : row.registro_relevo === 'APP' 
                                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border border-green-200 hover:border-green-300'
                                  : 'bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 border border-indigo-200 hover:border-indigo-300'
                            }`}
                          >
                            <div className="flex flex-col">
                              {row.HORA_RELEVO ? (
                                row.HORA_RELEVO === 'PENDIENTE' ? (
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="text-xs font-semibold leading-tight text-orange-900 group-hover:text-orange-700">
                                      PENDIENTE
                                    </span>
                                    <svg className="w-3 h-3 transition-colors flex-shrink-0 text-orange-400 group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex items-center justify-between gap-1">
                                      <span className={`text-xs font-semibold leading-tight ${
                                        row.registro_relevo === 'APP'
                                          ? 'text-green-900 group-hover:text-green-700'
                                          : 'text-indigo-900 group-hover:text-indigo-700'
                                      }`}>
                                        {row.HORA_RELEVO.split(' ')[1] || row.HORA_RELEVO}
                                      </span>
                                      <svg className={`w-3 h-3 transition-colors flex-shrink-0 ${
                                        row.registro_relevo === 'APP'
                                          ? 'text-green-400 group-hover:text-green-600'
                                          : 'text-indigo-400 group-hover:text-indigo-600'
                                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </div>
                                    <div className="flex items-center mt-0.5">
                                      <span className={`text-xs font-medium leading-tight ${
                                        row.registro_relevo === 'APP'
                                          ? 'text-green-600'
                                          : 'text-purple-600'
                                      }`}>
                                        {row.registro_relevo === 'PLATAFORMA' ? 'SOC' : (row.registro_relevo || '-')}
                                      </span>
                                    </div>
                                  </>
                                )
                              ) : (
                                <span className="text-xs text-gray-500">Sin información</span>
                              )}
                            </div>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal con información adicional */}
        {showModal && selectedRow && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">Detalles del Relevo</h3>
                      <p className="text-sm text-gray-600 mt-0.5">Información completa del registro</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setGuardiasAsignados([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                    aria-label="Cerrar"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
                  </button>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-4">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <div className="space-y-2.5">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Punto</span>
                      <p className="text-xs font-medium text-gray-900 mt-0.5">{selectedRow.punto ? `${environment.puntoChar || ''}${selectedRow.punto}` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</span>
                      <p className="text-xs font-medium text-gray-900 mt-0.5 break-words">{selectedRow.descripcion || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre</span>
                      <p className="text-xs font-medium text-gray-900 mt-0.5 break-words">{selectedRow.nombre || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ciudad</span>
                      <p className="text-xs font-medium text-gray-900 mt-0.5">{selectedRow.ciudad ? selectedRow.ciudad.toUpperCase() : '-'}</p>
                    </div>
                    {selectedRow.HORA_RELEVO !== 'PENDIENTE' && (
                      <>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hora Relevo</span>
                          <p className="text-xs font-medium text-gray-900 mt-0.5">{selectedRow.HORA_RELEVO || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Registro Relevo</span>
                          <p className="text-xs font-medium text-gray-900 mt-0.5">
                            {selectedRow.registro_relevo === 'PLATAFORMA' ? 'SOC' : (selectedRow.registro_relevo || '-')}
                          </p>
                        </div>
                      </>
                    )}
                    {selectedRow.HORA_RELEVO === 'PENDIENTE' && (
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Guardias Asignados</span>
                        {loadingGuardias ? (
                          <div className="mt-2 flex items-center justify-center py-2">
                            <svg className="animate-spin h-3 w-3 text-indigo-600" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="ml-2 text-[10px] text-gray-600">Cargando...</span>
                          </div>
                        ) : guardiasAsignados.length > 0 ? (
                          <div className="mt-2 space-y-2">
                            {guardiasAsignados.map((guardia, index) => {
                              // Limpiar el número de teléfono para el enlace tel:
                              const telefonoLimpio = guardia.telefono && guardia.telefono !== 'N/A' 
                                ? guardia.telefono.replace(/[\s\-\(\)]/g, '')
                                : null;
                              const puedeLlamar = telefonoLimpio && telefonoLimpio.length > 0;
                              
                              return (
                                <div key={guardia.cedula || index} className="bg-white rounded-lg p-1.5 border border-gray-200">
                                  <p className="text-[10px] leading-tight font-medium text-gray-900 break-words">{guardia.nombre_completo || '-'}</p>
                                  <div className="flex items-center justify-between mt-0.5 gap-1">
                                    <p className="text-[10px] text-gray-600 flex-1 min-w-0">
                                      <span className="font-medium">Tel:</span> <span className="break-all">{guardia.telefono || 'N/A'}</span>
                                    </p>
                                    {puedeLlamar && (
                                      <a
                                        href={`tel:${telefonoLimpio}`}
                                        className="ml-1 flex-shrink-0 p-1 bg-green-500 hover:bg-green-600 rounded-full transition-colors duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
                                        title={`Llamar a ${guardia.nombre_completo}`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                      </a>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[10px] text-gray-500 mt-0.5 italic">No hay guardias asignados a este puesto</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer con botón */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ Modal 1: Asignar Doblada ============ */}
        {controlAutomatico && dobladaOpen && dobladaRow && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                cerrarModalDoblada();
              }
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Asignar Doblada</h3>
                      <p className="text-xs text-gray-600">Selecciona quién cubrió este puesto</p>
                    </div>
                  </div>
                  <button
                    onClick={cerrarModalDoblada}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none rounded-full p-1"
                    aria-label="Cerrar"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1.5">
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-gray-500">Punto</span>
                    <span className="font-medium text-gray-900 text-right">
                      {dobladaRow.punto ? `${environment.puntoChar || ''}${dobladaRow.punto}` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-gray-500">Proyecto</span>
                    <span className="font-medium text-gray-900 text-right break-words">{dobladaRow.nombre || '-'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-gray-500">Fecha / Turno</span>
                    <span className="font-medium text-gray-900 text-right">
                      {fechaServidor || '-'} · {turno || '-'}
                    </span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1">En turno</p>
                  <p className="text-sm font-semibold text-amber-900">
                    {nombrePlanificadoFila(dobladaRow) || 'Sin información en cronograma'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Asignar doblada</label>
                  <input
                    type="text"
                    value={dobladaSelected ? dobladaSelected.nombreCompleto : dobladaSearch}
                    onChange={(e) => {
                      setDobladaSelected(null);
                      setDobladaSearch(e.target.value);
                      setDobladaShowResults(true);
                    }}
                    placeholder="Buscar por apellido, nombre o cédula..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                  {dobladaShowResults && !dobladaSelected && dobladaSearch.trim().length > 0 && (
                    <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm max-h-64 overflow-y-auto">
                      {dobladaLoadingResults ? (
                        <div className="px-3 py-2 text-xs text-gray-500 italic">Cargando...</div>
                      ) : dobladaResults.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500 italic">Sin coincidencias</div>
                      ) : (
                        dobladaResults.map((p) => (
                          <button
                            key={p.cedula}
                            type="button"
                            onClick={() => {
                              setDobladaSelected(p);
                              setDobladaShowResults(false);
                            }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{p.nombreCompleto || '—'}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {dobladaError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                    {dobladaError}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-2 flex-shrink-0">
                <button
                  onClick={cerrarModalDoblada}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (!dobladaSelected) {
                      setDobladaError('Selecciona un vigilante de la lista.');
                      return;
                    }
                    setDobladaError('');
                    setDobladaConfirmOpen(true);
                  }}
                  disabled={!dobladaSelected}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-md hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ Modal 2: Confirmación ============ */}
        {controlAutomatico && dobladaConfirmOpen && dobladaRow && dobladaSelected && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !dobladaSubmitting) {
                setDobladaConfirmOpen(false);
              }
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                <h3 className="text-lg font-bold text-gray-900">Confirmar doblada</h3>
                <p className="text-xs text-gray-600 mt-0.5">Revisa los datos antes de registrar</p>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-800 leading-relaxed">
                  Registrar doblada para{' '}
                  <span className="font-semibold text-amber-700">{dobladaSelected.nombreCompleto}</span>
                  {' '}en el punto{' '}
                  <span className="font-semibold text-gray-900">
                    {dobladaRow.punto ? `${environment.puntoChar || ''}${dobladaRow.punto}` : '-'}
                  </span>
                  {' '}el día{' '}
                  <span className="font-semibold text-gray-900">{fechaServidor || '-'}</span>
                  {' '}turno{' '}
                  <span className="font-semibold text-gray-900">{turno || '-'}</span>
                  {' '}por{' '}
                  <span className="font-semibold text-amber-700">
                    {nombrePlanificadoFila(dobladaRow) || '—'}
                  </span>
                  .
                </p>
                {dobladaError && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                    {dobladaError}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-2">
                <button
                  onClick={() => setDobladaConfirmOpen(false)}
                  disabled={dobladaSubmitting}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Revisar
                </button>
                <button
                  onClick={submitDoblada}
                  disabled={dobladaSubmitting}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg shadow-md hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {dobladaSubmitting && (
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {dobladaSubmitting ? 'Registrando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ Modal 3: Éxito ============ */}
        {controlAutomatico && dobladaSuccessOpen && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-70 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setDobladaSuccessOpen(false);
                cerrarModalDoblada();
              }
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="px-6 py-6 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Doblada registrada correctamente</h3>
                <p className="text-xs text-gray-600 mt-1">La información se actualizó en el listado.</p>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => {
                    setDobladaSuccessOpen(false);
                    cerrarModalDoblada();
                  }}
                  className="w-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg shadow-md hover:from-indigo-700 hover:to-indigo-800"
                >
                  Listo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ControlRelevos;

